import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);

const CART_KEY = "cart:v1";
const DISC_KEY = "cart:discount";

const num = (v, d = 0) => {
  const n = typeof v === "string" ? parseFloat(v.replace(/[, ]/g, "")) : Number(v);
  return Number.isFinite(n) ? n : d;
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch { return []; }
  });
  const [discount, setDiscount] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISC_KEY) || "null"); } catch { return null; }
  });

  useEffect(() => { try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch {} }, [items]);
  useEffect(() => { try { localStorage.setItem(DISC_KEY, JSON.stringify(discount)); } catch {} }, [discount]);

  /** Build stable key from product id/sku/name + size */
  const buildKey = (product, size) => {
    const id = product?.id ?? product?.sku ?? product?.name ?? "item";
    return `${id}${size ? `-${size}` : ""}`;
  };

  /** Add (merges by id+size) */
  const add = (product, qty = 1, size = null) => {
    const key = buildKey(product, size);
    const unit = num(product?.offerPrice ?? product?.price, 0);

    setItems(prev => {
      const i = prev.findIndex(x => x.key === key);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: num(next[i].qty, 0) + num(qty, 1) };
        return next;
      }
      return [
        ...prev,
        {
          key,
          id: product?.id,
          name: product?.name ?? product?.title ?? "Unnamed",
          price: unit,
          qty: num(qty, 1),
          image: product?.image ?? product?.images?.[0] ?? null,
          size,
        },
      ];
    });
  };

  /** Update quantity by row key (or item object with .key) */
  const updateQty = (keyOrItem, qty) => {
    const k = typeof keyOrItem === "string" ? keyOrItem : keyOrItem?.key;
    if (!k) return;
    setItems(prev => {
      if (num(qty, 0) <= 0) return prev.filter(x => x.key !== k);
      return prev.map(x => (x.key === k ? { ...x, qty: num(qty, 1) } : x));
    });
  };

  const inc = (keyOrItem) => {
    const k = typeof keyOrItem === "string" ? keyOrItem : keyOrItem?.key;
    if (!k) return;
    setItems(prev =>
      prev.map(x => (x.key === k ? { ...x, qty: num(x.qty, 1) + 1 } : x))
    );
  };

  const dec = (keyOrItem) => {
    const k = typeof keyOrItem === "string" ? keyOrItem : keyOrItem?.key;
    if (!k) return;
    setItems(prev =>
      prev.map(x =>
        x.key === k ? { ...x, qty: Math.max(1, num(x.qty, 1) - 1) } : x
      )
    );
  };

  const remove = (key) => setItems(prev => prev.filter(x => x.key !== key));
  const clear = () => setItems([]);
  const clearDiscount = () => setDiscount(null);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + num(it.price) * num(it.qty, 1), 0),
    [items]
  );

  const discountAmount = useMemo(() => {
    if (!discount) return 0;
    if (discount.type === "percent") return Math.round(subtotal * (num(discount.value, 0) / 100));
    if (discount.type === "amount") return Math.min(subtotal, num(discount.value, 0));
    // Support alt shapes: { percent, amount }
    const pct = num(discount.percent, 0);
    const amt = num(discount.amount, 0);
    const pcut = pct > 0 ? (subtotal * pct) / 100 : 0;
    return Math.min(subtotal, Math.max(pcut, amt, 0));
  }, [discount, subtotal]);

  const total = Math.max(0, subtotal - discountAmount);

  const value = {
    items,
    add,
    remove,
    clear,
    updateQty,
    inc,
    dec,
    subtotal,
    discount,
    setDiscount,
    clearDiscount,
    discountAmount,
    total,
  };

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
